import { useEffect, useState } from 'react';
import styles from './LoadingState.module.css';

/**
 * Ported from spectrum-ui's `LoadingState` block — the unmodified source sits in
 * `reference/spectrum-ui/ai-assistants/loading-state.tsx` for diffing. Upstream
 * is Tailwind with inline `style` objects; nothing of either survives here.
 * Every value resolves to a token except the loop durations and the opacity
 * stops, which no variable in the file can express.
 *
 * Figma: `Loading State` (842:26194) — drawn from this file after the fact, as
 * planned. It carries `Pattern=Drive|Dots|Orbit` and text properties for the
 * label and the timer, and its cells bind `text/primary` at full strength with
 * the orbit centre at 0.15.
 *
 * `Card`'s `State=Loading` variant (904:35112) is the file's only consumer, and
 * it takes the component two ways past what the set draws: the label and the
 * timer layers are switched off, and the cells are recoloured blue. Figma does
 * both as one-off overrides on the instance rather than as variants, because a
 * single instance does not justify a variant axis. Code cannot borrow that
 * shape — an override has no name to call it by — so the two arrive here as
 * `tone` and the two `show*` flags. See the stylesheet for the colour.
 *
 * All three upstream patterns are ported. They differ only in which cells light
 * up and when, so they cost one class each rather than a second component.
 *
 * THREE THINGS GO PAST UPSTREAM:
 *
 * 1. The whole row is a `role="status"` live region, so a screen reader is told
 *    the thing is working. Upstream labels only the timer, which means the
 *    arrival of a loading state announces nothing at all.
 *
 * 2. The timer is pinned `aria-live="off"`. `role="timer"` implies that already,
 *    but it sits inside the live region added above and inheritance here is not
 *    something to leave to chance — a value reannounced every 100ms is unusable.
 *
 * 3. Under reduced motion the grid rests at full strength rather than at the
 *    animation's trough. Upstream stops the animation and leaves every cell at
 *    `opacity: 0.15`, which is the same class of defect as a shimmer that hands
 *    back a transparent fill: the indicator is still there and nearly invisible.
 *    The idle/active contrast is preserved, so `Orbit`'s hollow centre still
 *    reads. See the reduced-motion block at the foot of the stylesheet.
 */

/** Upstream names this `variant`; `pattern` is what the three values actually are. */
export type LoadingPattern = 'drive' | 'dots' | 'orbit';

/**
 * Figma recolours the grid by overriding the instance's fills; `tone` is the
 * named version of that. `accent` is what `Card`'s loading state uses.
 */
export type LoadingTone = 'default' | 'accent';


export type LoadingStateProps = {
  /**
   * The word that shimmers. Upstream defaults to "Churning", which is
   * spectrum-ui's voice rather than this system's.
   */
  label?: string;
  pattern?: LoadingPattern;
  /**
   * Figma's fill override on the `Card` instance. `default` is the grey the
   * standalone component draws; `accent` is `component/loading-state/dot-accent`.
   */
  tone?: LoadingTone;
  /**
   * Figma hides the `Label` layer inside `Card`. Set false for the grid alone —
   * the row then names itself for assistive tech, since the word that used to do
   * that is gone.
   */
  showLabel?: boolean;
  /**
   * Figma hides the `Elapsed` layer inside `Card`. Set false to drop the timer —
   * which also stops its interval rather than merely hiding it, so a loading
   * card is not re-rendering ten times a second to paint nothing.
   */
  showElapsed?: boolean;
  /**
   * Set false to hold the grid still, at the same full-strength rest the
   * reduced-motion rule uses. `Reasoning Trace`'s `Status=Complete` variants
   * (750:10938) draw the grid after the work is done; a grid still pulsing
   * there would claim the agent is still working.
   */
  animate?: boolean;
  className?: string;
};

/**
 * Upstream's `chevron`: a 3x3 of delays that sweeps left to right with the
 * middle row leading, producing [90, 180, 270, 0, 90, 180, 90, 180, 270].
 * Derived rather than written out, as upstream does, because the derivation is
 * the thing worth keeping — the delay is the cell's distance from the middle-left.
 */
const CHEVRON: (number | null)[] = Array.from({ length: 9 }, (_, i) => {
  const row = Math.floor(i / 3);
  const col = i % 3;
  return (col + Math.abs(row - 1)) * 90;
});

/** The perimeter, clockwise from the top-left. The centre cell never lights. */
const ORBIT_ORDER = [0, 1, 2, 5, 8, 7, 6, 3];
const ORBIT: (number | null)[] = Array.from({ length: 9 }, (_, i) => {
  const k = ORBIT_ORDER.indexOf(i);
  return k === -1 ? null : k * 110;
});

const PATTERNS: Record<LoadingPattern, { delays: (number | null)[]; round: boolean }> = {
  drive: { delays: CHEVRON, round: false },
  dots: { delays: CHEVRON, round: true },
  orbit: { delays: ORBIT, round: false },
};

/**
 * Tenths of a second since mount, formatted the way upstream formats it —
 * `1.2s` under a minute, `1m 4.2s` past it.
 *
 * Counts in tenths and divides on the way out rather than holding a float, for
 * the same reason `ReasoningTrace`'s clock does: accumulating `+= 0.1` drifts
 * off binary fractions within a few seconds and starts rendering 4.300000000000001.
 * The two are not shared because they differ in what they do — that one resets
 * when its `active` flag flips, this one runs from mount.
 */
function useElapsed(enabled: boolean): string {
  const [tenths, setTenths] = useState(0);

  useEffect(() => {
    /*
     * Not merely hidden — not started. `Card`'s loading state never shows the
     * timer, and an interval firing ten times a second to drive a value nothing
     * reads keeps a card re-rendering for as long as it is on screen.
     */
    if (!enabled) return;
    const id = setInterval(() => setTenths((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [enabled]);

  const total = tenths / 10;
  if (total < 60) return `${total.toFixed(1)}s`;
  return `${Math.floor(total / 60)}m ${(total % 60).toFixed(1)}s`;
}

export function LoadingState({
  label = 'Loading',
  pattern = 'drive',
  tone = 'default',
  showLabel = true,
  showElapsed = true,
  animate = true,
  className,
}: LoadingStateProps) {
  const elapsed = useElapsed(showElapsed);
  const { delays, round } = PATTERNS[pattern];

  return (
    <div
      role="status"
      /*
       * Not in Figma — the file has no notion of an accessible name. With the
       * label hidden the live region has nothing left to announce, so it takes
       * the same word as a name instead. Left off when the label is visible:
       * naming a region that already contains the text makes a screen reader
       * say it twice.
       */
      aria-label={showLabel ? undefined : label}
      className={[
        styles.loader,
        styles[pattern],
        tone === 'accent' ? styles.accent : null,
        animate ? null : styles.still,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/*
        * Decorative: the label beside it already says what is happening, and
        * nine animated cells have nothing to announce.
        */}
      <span aria-hidden className={styles.grid}>
        {delays.map((delay, i) => (
          <span
            key={i}
            className={[
              delay === null ? styles.cellIdle : styles.cell,
              round ? styles.cellRound : null,
            ]
              .filter(Boolean)
              .join(' ')}
            /*
             * The per-cell delay is the only thing that varies between the nine,
             * so it rides in on a custom property rather than nine classes.
             */
            style={delay === null ? undefined : ({ '--ls-delay': `${delay}ms` } as React.CSSProperties)}
          />
        ))}
      </span>

      {/*
        * A plain span rather than `Text`, following `ReasoningTrace`'s shimmering
        * label and Button's `.label`: the fill here is a gradient that `Text`
        * cannot express, and `Text`'s colour class would argue with it at equal
        * specificity.
        */}
      {showLabel && <span className={styles.label}>{label}</span>}

      {showElapsed && (
        <span
          role="timer"
          aria-live="off"
          aria-label={`Elapsed time ${elapsed}`}
          className={styles.elapsed}
        >
          {elapsed}
        </span>
      )}
    </div>
  );
}
